import { inject } from "@angular/core";
import { getDownloadURL, ref, Storage, StorageError, uploadBytesResumable, UploadTaskSnapshot } from "@angular/fire/storage";

/**
 * Service responsible for handling file storage operations using Firebase Storage
 * Provides methods for uploading and managing files in cloud storage
 */
export class StorageService {

  //#region Storage Materials
  /**
   * Firebase Storage instance injected to handle cloud storage operations
   * Used for all file upload and download operations in the application
   */
  readonly #storage: Storage = inject(Storage);
  //#endregion

  //#region Constructor
  /**
   * A service to handle storage operations and states.
   * Initializes the storage service without requiring additional dependencies
   */
  constructor() { }
  //#endregion

  //#region File Upload Methods
  /**
   * Method overload for uploading a file and returning its URL
   * 
   * @param path The storage path where the file should be saved
   * @param data The file or blob data to upload
   * @param returnUrl Set to true to return the download URL of the uploaded file
   * @param progressCallback Optional callback function to track upload progress
   * @returns Promise with the download URL of the uploaded file
   */
  protected uploadFile(path: string, data: Blob | File, returnUrl: true, progressCallback?: (value: number) => any): Promise<string>;
  
  /**
   * Method overload for uploading a file without returning its URL
   * 
   * @param path The storage path where the file should be saved
   * @param data The file or blob data to upload
   * @param returnUrl Set to false to not return the download URL
   * @param progressCallback Optional callback function to track upload progress
   * @returns Promise with void result (no URL returned)
   */
  protected uploadFile(path: string, data: Blob | File, returnUrl?: false, progressCallback?: (value: number) => any): Promise<void>;
  
  /**
   * Implementation of the file upload process to Firebase Storage
   * 
   * @param path The storage path where the file should be saved
   * @param data The file or blob data to upload
   * @param returnUrl Whether to return the download URL after successful upload
   * @param progressCallback Optional callback function to track upload progress
   * @returns Promise with either the download URL or void based on returnUrl parameter
   */
  protected uploadFile(path: string, data: Blob | File, returnUrl?: boolean, progressCallback?: (value: number) => any): Promise<string | void> {
    return new Promise((resolve, reject) => {
      // Create a reference for 'path' and upload file.
      const fileRef = ref(this.#storage, path);
      const fileUploadTask = uploadBytesResumable(fileRef, data);

      // Listen for state changes, errors, and completion of the upload.
      fileUploadTask.on('state_changed',
        /**
         * Progress callback that handles different states of the upload process
         * Updates the progress and logs the current state
         * 
         * @param snapshot Current state of the upload task
         */
        (snapshot: UploadTaskSnapshot) => {
          // Observe state change events such as progress, pause, and resume.
          switch (snapshot.state) {
            case 'canceled':
              console.log('The upload has been canceled.');
              break;
            case 'paused':
              console.log('The upload is currently paused.');
              break;
            case 'running':
              console.log('The upload is in progress.');
              break;
            case 'success':
              console.log('The upload has completed successfully.');
              break;
            case 'error':
              console.log('An error occurred during the upload.');
              break;
          }

          // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded.
          // Calculate and report progress percentage if a callback was provided
          progressCallback?.((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        },
        /**
         * Error callback that handles upload errors
         * 
         * @param error Storage error object from Firebase
         */
        (error: StorageError) => {
          // A full list of error codes is available at
          // https://firebase.google.com/docs/storage/web/handle-errors
          switch (error.code) {
            case 'storage/unauthorized':
              // User doesn't have permission to access the object
              break;
            case 'storage/canceled':
              // User canceled the upload
              break;

            // ...

            case 'storage/unknown':
              // Unknown error occurred, inspect error.serverResponse
              break;
          }

          // Return error.
          reject(error)
        },
        /**
         * Success callback that resolves the promise
         * Returns the download URL if returnUrl is true, otherwise undefined
         */
        () => resolve(returnUrl ? getDownloadURL(fileUploadTask.snapshot.ref) : undefined)
      );
    });
  }
  //#endregion
}